<template>
	<div class="TicketOptionExternalSystemInfoSys" v-if="ticketOption">
		<div v-if="infosystemMixedTypeError" class="mixedTypeError">
			<v-icon color="error">mdi-alert-circle</v-icon>
			<span>{{ infosystemMixedTypeError }}</span>
		</div>
		<div class="row">
			<Field>
				<mys-switch
					v-model="ticketOption.fields.external.isTransportationProduct"
					:label="$t('text.transportationProduct')"
				/>
			</Field>
		</div>
		<div class="row">
			<Field :title="$t('text.infosysCategoryId')">
				<v-text-field v-model="ticketOption.fields.external.categoryId"
					variant="outlined" style="background-color:#ffffff"
					:error-messages="infosystemCategoryIdError"
					:hide-details="!infosystemCategoryIdError"
				/>
			</Field>
			<Field :title="$t('text.infosysProductId')">
				<v-text-field v-model="ticketOption.fields.external.productId"
					variant="outlined" style="background-color:#ffffff"
					:error-messages="infosystemProductIdError"
					:hide-details="!infosystemProductIdError"
				/>
			</Field>
		</div>
		<div class="row">
			<Field :title="$t('text.infosysExternalProductReference')">
				<v-text-field v-model="ticketOption.fields.external.externalProductReference"
					variant="outlined" style="background-color:#ffffff"
					:error-messages="infosystemExternalProductReferenceError"
					:hide-details="!infosystemExternalProductReferenceError"
				/>
			</Field>
		</div>
		<div class="row">
			<Field :title="$t('text.infosysFromStation')">
				<v-text-field v-model="ticketOption.fields.external.fromStation"
					variant="outlined" style="background-color:#ffffff"
					:error-messages="infosystemFromStationError"
					:hide-details="!infosystemFromStationError"
				/>
			</Field>
			<Field :title="$t('text.infosysToStation')">
				<v-text-field v-model="ticketOption.fields.external.toStation"
					variant="outlined" style="background-color:#ffffff"
					:error-messages="infosystemToStationError"
					:hide-details="!infosystemToStationError"
				/>
			</Field>
		</div>
	</div>
</template>

<script lang="ts">
import Common from '../../mixins/Common.vue'

export default {
	mixins: [ Common ],
	props: {
		ticketOption: Object,
		product: Object,
	},
	computed: {
		// Category ID: optional in all cases
		infosystemCategoryIdError(): string {
			return ''
		},
		// Product ID: mandatory when NOT transportation product, optional when IS transportation product
		infosystemProductIdError(): string {
			if (this.ticketOption?.fields?.external?.isTransportationProduct) return ''
			if (!this.userIsOperator && this.spTrustLevel <= 2) return ''
			return !this.ticketOption?.fields.external.productId?.length ? this.$t('text.required') : ''
		},
		// External Product Reference: optional when NOT transportation product, mandatory when IS transportation product
		infosystemExternalProductReferenceError(): string {
			if (!this.ticketOption?.fields?.external?.isTransportationProduct) return ''
			if (!this.userIsOperator && this.spTrustLevel <= 2) return ''
			return !this.ticketOption?.fields.external.externalProductReference?.length ? this.$t('text.required') : ''
		},
		// From Station: mandatory when IS transportation product, optional when NOT transportation product
		infosystemFromStationError(): string {
			if (!this.ticketOption?.fields?.external?.isTransportationProduct) return ''
			if (!this.userIsOperator && this.spTrustLevel <= 2) return ''
			return !this.ticketOption?.fields.external.fromStation?.length ? this.$t('text.required') : ''
		},
		// To Station: mandatory when IS transportation product, optional when NOT transportation product
		infosystemToStationError(): string {
			if (!this.ticketOption?.fields?.external?.isTransportationProduct) return ''
			if (!this.userIsOperator && this.spTrustLevel <= 2) return ''
			return !this.ticketOption?.fields.external.toStation?.length ? this.$t('text.required') : ''
		},
		// Mixed transport/standard validation: all ticket options must have same isTransportationProduct value
		infosystemMixedTypeError(): string {
			const allTicketOptions = this.product?.fields?.ticket?.de?.fields?.ticketOptions?.de || []
			if (allTicketOptions.length <= 1) return ''

			// Get all isTransportationProduct values that are explicitly set
			const transportFlags = allTicketOptions
				.map(option => option.fields?.external?.isTransportationProduct)
				.filter(flag => flag !== undefined && flag !== null)

			if (transportFlags.length <= 1) return ''

			// Check if there's a mix of true and false values
			const hasTransport = transportFlags.some(flag => flag === true)
			const hasStandard = transportFlags.some(flag => flag === false)

			if (hasTransport && hasStandard) {
				return this.$t('text.infosysMixedTypeError')
			}
			return ''
		},
		valid() {
			// Check for mixed transport/standard types first (applies to all)
			if (this.infosystemMixedTypeError) return false

			if (this.ticketOption?.fields?.external?.isTransportationProduct) {
				// Transportation product: external product reference, from station, to station are mandatory
				return !this.infosystemExternalProductReferenceError
					&& !this.infosystemFromStationError
					&& !this.infosystemToStationError
			}
			else {
				// Non-transportation product: product ID is mandatory
				return !this.infosystemProductIdError
			}
		},
	},
	methods: {
		async setExternalSystem() {
			if (!this.ticketOption) return
			this.ticketOption.fields.external = {
				isTransportationProduct: this.ticketOption.fields.external.isTransportationProduct ?? false,
				categoryId: this.ticketOption.fields.external.categoryId ?? null,
				productId: this.ticketOption.fields.external.productId ?? null,
				externalProductReference: this.ticketOption.fields.external.externalProductReference ?? null,
				fromStation: this.ticketOption.fields.external.fromStation ?? null,
				toStation: this.ticketOption.fields.external.toStation ?? null,
			}
		},
	},
	beforeMount() {
		if (!this.ticketOption?.fields.external) {
			if (this.ticketOption?.fields) {
				this.ticketOption.fields.external = {
					isTransportationProduct: false,
					categoryId: null,
					productId: null,
					externalProductReference: null,
					fromStation: null,
					toStation: null,
				}
			}
		}
	},
	async mounted() {
		await this.setExternalSystem()
	},
}
</script>

<style scoped>
.row { display: flex; gap: 10px; width: 100%; }
.row > * { flex: 1 0; }
.row > .Field { border-left: none; padding: 0; }
.mixedTypeError { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background-color: #ffebee; border: 1px solid #ef5350; border-radius: 4px; color: #c62828; margin-bottom: 8px; }
</style>
